import type {
  InsuranceConnectionWithContact,
  InsuranceWithConnections,
} from "./templateDictionaryOptions";
import {
  formatAddressLine1,
  formatAddressLine2,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";

type InsuranceVariablePrefix = "clientInsurance" | "defendantInsurance";

const INSURANCE_CONTACT_ROLES = [
  "Insured",
  "PolicyHolder",
  "Insurer",
  "BodilyInjuryAdjuster",
  "PropertyDamageAdjuster",
  "MedPayAdjuster",
  "AdjusterSupervisor",
  "Adjuster",
  "SubrogationVendor",
] as const;

type InsuranceContactRole = (typeof INSURANCE_CONTACT_ROLES)[number];

interface InsuranceFieldMetadata {
  prefix: InsuranceVariablePrefix;
  contactRole: InsuranceContactRole;
  field: string;
}

const getFirst = <T>(items: T[] | undefined) => {
  if (!items || items.length === 0) return undefined;
  return items[0];
};

const parseInsuranceFieldMetadata = (
  variable: string,
): InsuranceFieldMetadata | undefined => {
  const prefix = variable.startsWith("clientInsurance")
    ? "clientInsurance"
    : variable.startsWith("defendantInsurance")
      ? "defendantInsurance"
      : undefined;
  if (!prefix) return undefined;

  const suffix = variable.slice(prefix.length);
  const contactRole = INSURANCE_CONTACT_ROLES.find((role) =>
    suffix.startsWith(role),
  );
  if (!contactRole) return undefined;

  const field = suffix.slice(contactRole.length) || "Name";
  return {
    prefix,
    contactRole,
    field,
  };
};

const getInsuranceContactFieldValue = (
  insurance: InsuranceWithConnections,
  metadata: InsuranceFieldMetadata,
): string => {
  let connection: InsuranceConnectionWithContact | undefined;
  switch (metadata.contactRole) {
    case "Insured":
      connection = insurance.insured;
      break;
    case "PolicyHolder":
      connection = insurance.policyHolder ?? insurance.insured;
      break;
    case "Insurer":
      connection = insurance.insurer;
      break;
    case "BodilyInjuryAdjuster":
      connection = insurance.bodilyInjuryAdjuster;
      break;
    case "PropertyDamageAdjuster":
      connection = insurance.propertyDamageAdjuster;
      break;
    case "MedPayAdjuster":
      connection = insurance.medPayAdjuster;
      break;
    case "AdjusterSupervisor":
      connection = insurance.adjusterSupervisor;
      break;
    case "Adjuster":
      connection =
        insurance.bodilyInjuryAdjuster ??
        insurance.propertyDamageAdjuster ??
        insurance.medPayAdjuster ??
        insurance.adjusterSupervisor;
      break;
    case "SubrogationVendor":
      connection = insurance.subrogationVendor;
      break;
  }

  if (!connection) return "";

  const primaryAddress = getFirst(connection.contact.addresses);
  const primaryEmail = getFirst(connection.contact.emails);
  const primaryPhone = getFirst(connection.contact.phones);
  const primaryFax = getFirst(connection.contact.faxes);

  switch (metadata.field) {
    case "":
    case "Name":
      return formatContactsName(connection.contact) || "";
    case "FirstName":
      return String(connection.contact.individualFirstName ?? "");
    case "LastName":
      return String(connection.contact.individualLastName ?? "");
    case "Email":
      return primaryEmail?.address ?? "";
    case "Phone":
      return formatLocalOrIntlPhoneNumber(primaryPhone);
    case "Fax":
      return formatLocalOrIntlPhoneNumber(primaryFax);
    case "AddressLine1":
      return formatAddressLine1({
        street1: primaryAddress?.street1 ?? undefined,
        street2: primaryAddress?.street2 ?? undefined,
      });
    case "AddressLine2":
      return formatAddressLine2({
        city: primaryAddress?.city ?? undefined,
        state: primaryAddress?.state ?? undefined,
        zipCode: primaryAddress?.zipCode ?? undefined,
      });
    default:
      return "";
  }
};

const getInsuranceNonContactValue = (
  insurance: InsuranceWithConnections,
  baseVariable: string,
  sectionKey: InsuranceVariablePrefix,
): string | undefined => {
  switch (baseVariable) {
    case `${sectionKey}PolicyNumber`:
      return insurance.policyNumber ?? "";
    case `${sectionKey}ClaimNumber`:
      return insurance.claimNumber ?? "";
    case `${sectionKey}GroupNumber`:
      return insurance.groupNumber ?? "";
    case `${sectionKey}MemberNumber`:
      return insurance.memberNumber ?? "";
    case `${sectionKey}PolicyLimits`:
      return insurance.policyLimits ?? "";
    case `${sectionKey}SubrogationFileNumber`:
      return insurance.subrogationFileNumber ?? "";
    default:
      return undefined;
  }
};

/** Match the insurance section's existing Apply Insurance behavior. */
export const getInsuranceTemplateFieldValue = (
  insurance: InsuranceWithConnections,
  baseVariable: string,
  prefix: InsuranceVariablePrefix,
): string | undefined => {
  const metadata = parseInsuranceFieldMetadata(baseVariable);
  return metadata && metadata.prefix === prefix
    ? getInsuranceContactFieldValue(insurance, metadata)
    : getInsuranceNonContactValue(insurance, baseVariable, prefix);
};
